import React, { useEffect, useMemo, useState } from "react";
import Card from "components/card";
import { useDispatch, useSelector } from "react-redux";
import { RiErrorWarningFill } from "react-icons/ri";
import OrderNotes from "./OrderNotes";
import SelectField from "components/fields/SelectField";
import Modal from "components/modal";
import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import { changeOrderStatus } from "store/orderSlice";
import { showToast } from "components/toast";

function BasicInfo({ refreshList }) {
  const dispatch = useDispatch();
  const { order: { afterDraft = {} } = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );
  const {
    orderDetailsData,
    orderDetailsLoading,
    orderDetailsError,
    orderStatusError,
    orderStatusData,
    orderStatusLoading,
    orderChangeStatusLoading,
    orderChangeStatusError,
  } = useSelector((state) => state.orders);

  const [pendingStatus, setPendingStatus] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const statusOptions = useMemo(
    () =>
      orderStatusData?.map((status) => ({
        label: status.name,
        value: status._id,
      })) || [],
    [orderStatusData]
  );

  const orderDetails = useMemo(
    () => [
      { label: "Order No", value: orderDetailsData?.orderNo || "N/A" },
      { label: "Order Type", value: orderDetailsData?.orderType || "N/A" },
      { label: "Description", value: orderDetailsData?.description || "N/A" },
      {
        label: "Status",
        value: orderDetailsData?.status?._id || "N/A",
        isStatus: true,
      },
      {
        label: "Created By",
        value: orderDetailsData?.createdByUser || "N/A",
      },
      {
        label: "Order For",
        value: orderDetailsData?.orderForUser || "N/A",
      },
      {
        label: "Created At",
        value: orderDetailsData?.createdAt
          ? new Date(orderDetailsData.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "N/A",
      },
      {
        label: "Updated At",
        value: orderDetailsData?.updatedAt
          ? new Date(orderDetailsData.updatedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "N/A",
      },
    ],
    [orderDetailsData]
  );

  const handleConfirmChange = async () => {
    if (!pendingStatus?.value || !orderDetailsData?._id) return;

    try {
      const res = await dispatch(
        changeOrderStatus({
          _id: orderDetailsData?._id,
          status: pendingStatus?.value,
        })
      ).unwrap();

      if (res?.statuscode === 200) {
        showToast.success(`Order ${pendingStatus?.label} successfully!`);

        refreshList();
        setIsConfirmModalOpen(false);
        setPendingStatus(null);
      } else {
        showToast.error(`Order ${pendingStatus?.label} failed!`);
        console.error(
          "Order status change failed:",
          res?.msg || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Order status change failed:", error);
      showToast.error(`Order ${pendingStatus?.label} failed!`);
    }
  };

  return (
    <Card extra={"w-full h-full p-3"}>
      <div className="grid grid-cols-1 gap-2 px-2 md:grid-cols-12">
        {/* First div (7 cols) */}
        <div className="md:col-span-7">
          <div className="mb-4 mt-2 flex w-full items-center justify-between">
            <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white">
              Order Details
            </h4>
          </div>

          <div className="grid grid-cols-2 gap-2 px-2 md:grid-cols-3">
            {orderDetails.map((item, index) => (
              <div
                key={index}
                className="flex flex-col items-start justify-center rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none"
              >
                <p className="text-sm text-gray-600">{item.label}</p>

                {item.isStatus ? (
                  // ✅ Status field logic
                  !orderDetailsLoading && !orderStatusLoading ? (
                    <SelectField
                      options={statusOptions}
                      className="w-full"
                      placeholder="Select Status"
                      error={orderStatusError}
                      disabled={!afterDraft?.statusChange}
                      value={
                        item.value
                          ? statusOptions.find(
                              (opt) => opt.value === item.value
                            )
                          : null
                      }
                      onChange={(selected) => {
                        setPendingStatus(selected);
                        setIsConfirmModalOpen(true);
                      }}
                    />
                  ) : (
                    // Skeleton while either detail/status is loading
                    <div className="mt-1 h-5 w-20 animate-pulse rounded-md bg-gray-100 dark:bg-navy-700"></div>
                  )
                ) : orderDetailsLoading ? (
                  // ✅ Skeleton loader for non-status fields
                  <div className="mt-1 h-5 w-10 animate-pulse rounded-md bg-gray-100 dark:bg-navy-700"></div>
                ) : orderDetailsError ? (
                  // ✅ Error state
                  <div className="flex items-center gap-2 text-red-500">
                    <RiErrorWarningFill className="h-4 w-4" />
                  </div>
                ) : (
                  // ✅ Normal text
                  <p className="text-base font-medium text-navy-700 dark:text-white">
                    {item.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Second div (5 cols) */}
        <div className="md:col-span-5">
          <OrderNotes />
        </div>
      </div>

      {/* Confirm Modal */}
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        className="max-w-md"
      >
        <h2 className="border-b py-3 pl-2 text-lg font-bold text-navy-700 dark:text-white">
          Confirm Status Change
        </h2>
        <div className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Are you sure you want to change the order status to{" "}
            <span className="font-semibold">{pendingStatus?.label}</span>?
          </p>
          {orderChangeStatusError && (
            <div className="mt-2">
              <p className="text-sm text-red-500">{orderChangeStatusError}</p>
            </div>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsConfirmModalOpen(false)}
            >
              Cancel
            </Button>
            <LoaderButton
              isLoading={orderChangeStatusLoading}
              onClick={handleConfirmChange}
            >
              Confirm
            </LoaderButton>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

export default BasicInfo;
